import { StyleSheet, View } from 'react-native';

import { Heading } from '@/components/Heading';
import { Input } from '@/components/Input';
import type { ShippingErrors } from '@/lib/voltrShipping';
import { spacing } from '@/constants/theme';
import type { VoltrShipping } from '@/types/voltr';

export type OrderShippingStepProps = {
  shipping: VoltrShipping;
  errors: ShippingErrors;
  onChange: (field: keyof VoltrShipping, value: string) => void;
};

/**
 * Order step 2 (Figma "Order 3"): where the brand ships it. No email field:
 * Voltr's address has none. The address lives in this screen's memory until
 * the order is sent and is never stored.
 */
export function OrderShippingStep({ shipping, errors, onChange }: OrderShippingStepProps) {
  const field = (name: keyof VoltrShipping) => ({
    value: shipping[name],
    error: errors[name],
    onChangeText: (value: string) => onChange(name, value),
  });
  return (
    <>
      <Heading size="3xl">Shipping Info</Heading>
      <View style={styles.fields} testID="voltr-shipping">
        {/* Labels and placeholders as in the Figma ("Order 3"). */}
        <Input label="Full name" placeholder="First Last" autoComplete="name" textContentType="name" {...field('name')} />
        <Input label="Street address" placeholder="Street address" autoComplete="street-address" textContentType="streetAddressLine1" {...field('line1')} />
        <Input label="Apt, suite (optional)" placeholder="Apt, suite" textContentType="streetAddressLine2" {...field('line2')} />
        <View style={styles.row}>
          <Input label="City" placeholder="City" textContentType="addressCity" style={styles.cell} {...field('city')} />
          <Input label="State" placeholder="State" autoCapitalize="characters" maxLength={2} textContentType="addressState" style={styles.cell} {...field('state')} />
          <Input label="ZIP" placeholder="00000" keyboardType="number-pad" maxLength={10} textContentType="postalCode" style={styles.cell} {...field('zip')} />
        </View>
        <Input label="Phone" placeholder="(000) 000 - 0000" keyboardType="phone-pad" textContentType="telephoneNumber" {...field('phone')} />
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  fields: { gap: spacing.md },
  // City | State | ZIP share one row in thirds, as in the Figma.
  row: { flexDirection: 'row', gap: spacing.md },
  cell: { flex: 1 },
});
